import { create } from "zustand";
import { persist } from "zustand/middleware";
import axios from "../utils/axios";

const syncCartEvent = async (productId, quantity) => {
  try {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");

    if (token && user) {
      await axios.post("/api/cart-events", {
        product_id: productId,
        quantity,
      });
    }
  } catch (err) {
    // Cart tracking should not block the customer's cart action.
  }
};

// Cart store for managing shopping cart state
export const useCartStore = create(
  persist(
    (set, get) => ({
      cart: [],

      // Add item to cart
      addToCart: async (product) => {
        const { cart } = get();
        const existingItem = cart.find((item) => item.id === product.id);
        let newQuantity = 1;
        if (existingItem) {
          newQuantity = existingItem.quantity + 1;
          set({
            cart: cart.map((item) =>
              item.id === product.id
                ? { ...item, quantity: newQuantity }
                : item
            ),
          });
        } else {
          set({
            cart: [
              ...cart,
              {
                ...product,
                price: Number(product.price || 0),
                quantity: 1,
                payment_link: product.payment_link || null,
                image_url: product.image_url || null,
              },
            ],
          });
        }
        await syncCartEvent(product.id, newQuantity);
      },

      // Remove item from cart
      removeFromCart: (productId) => {
        set({
          cart: get().cart.filter((item) => item.id !== productId),
        });
      },

      // Update item quantity
      updateQuantity: async (productId, quantity) => {
        if (quantity <= 0) {
          get().removeFromCart(productId);
          return;
        }

        set({
          cart: get().cart.map((item) =>
            item.id === productId ? { ...item, quantity } : item
          ),
        });

        await syncCartEvent(productId, quantity);
      },

      // Clear cart
      clearCart: () => {
        set({ cart: [] });
      },

      // Get cart total
      getCartTotal: () => {
        const { cart } = get();
        return cart.reduce(
          (total, item) => total + Number(item.price || 0) * item.quantity,
          0
        );
      },

      // Get cart item count
      getCartItemCount: () => {
        const { cart } = get();
        return cart.reduce((count, item) => count + item.quantity, 0);
      },

      // Get cart items for API
      getCartItems: () => {
        const { cart } = get();
        return cart.map((item) => ({
          productId: item.id,
          quantity: item.quantity,
          price: Number(item.price || 0),
        }));
      },
    }),
    {
      name: "tamilsandhai-cart", // name of the item in the storage (must be unique)
      partialize: (state) => ({ cart: state.cart }), // only persist the cart array
    }
  )
);

// Auth store for user authentication
export const useAuthStore = create((set, get) => ({
  user: null,
  token: localStorage.getItem("tamilsandhai-token"),
  isAuthenticated: false,

  // Login
  login: (userData, token) => {
    localStorage.setItem("tamilsandhai-token", token);
    set({
      user: userData,
      token,
      isAuthenticated: true,
    });
  },

  // Logout
  logout: () => {
    localStorage.removeItem("tamilsandhai-token");
    set({
      user: null,
      token: null,
      isAuthenticated: false,
    });
  },

  // Update user data
  updateUser: (userData) => {
    set({
      user: { ...get().user, ...userData },
    });
  },

  // Check if user has role
  hasRole: (role) => {
    const { user } = get();
    return user?.role === role;
  },
}));

// UI store for managing UI state
export const useUIStore = create((set) => ({
  isMobileMenuOpen: false,
  isCartOpen: false,

  // Toggle mobile menu
  toggleMobileMenu: () => {
    set((state) => ({ isMobileMenuOpen: !state.isMobileMenuOpen }));
  },

  // Close mobile menu
  closeMobileMenu: () => {
    set({ isMobileMenuOpen: false });
  },

  // Toggle cart
  toggleCart: () => {
    set((state) => ({ isCartOpen: !state.isCartOpen }));
  },

  // Close cart
  closeCart: () => {
    set({ isCartOpen: false });
  },
}));
